import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  CircularProgress,
  Chip,
  Button,
  Avatar,
  Fade,
  Card,
  Badge
} from '@mui/material';
import { 
  Add as AddIcon, 
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  SwapHoriz as TransferIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  List as ListIcon
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import DataTable, { Column, Action } from '../components/common/DataTable';
import { TransferService } from '../services/transfer.service';
import { DataTransfer, TransferStatus } from '../types/transfer.types';
import dayjs from 'dayjs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transfers-tabpanel-${index}`}
      aria-labelledby={`transfers-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function TransferList() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [allTransfers, setAllTransfers] = useState<DataTransfer[]>([]);
  const [sentTransfers, setSentTransfers] = useState<DataTransfer[]>([]);
  const [receivedTransfers, setReceivedTransfers] = useState<DataTransfer[]>([]);
  const [loading, setLoading] = useState<{all: boolean; sent: boolean; received: boolean}>({
    all: true,
    sent: true,
    received: true
  });
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    fetchTransfers();
  }, []);
  
  const fetchTransfers = async () => {
    try {
      setLoading(prev => ({ ...prev, all: true }));
      const all = await TransferService.getAllTransfers();
      setAllTransfers(all);
      
      setLoading(prev => ({ ...prev, sent: true }));
      const sent = await TransferService.getSentTransfers();
      setSentTransfers(sent);
      
      setLoading(prev => ({ ...prev, received: true }));
      const received = await TransferService.getReceivedTransfers();
      setReceivedTransfers(received);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки передач');
    } finally {
      setLoading({ all: false, sent: false, received: false });
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleCreateTransfer = () => {
    navigate('/transfers/create');
  };
  
  const handleViewTransfer = (id: string) => {
    navigate(`/transfers/${id}`);
  };
  
  const handleUpdateStatus = async (id: string, status: TransferStatus) => {
    try {
      const updatedTransfer = await TransferService.updateTransferStatus(id, { status });
      
      setAllTransfers(allTransfers.map(t => t.id === id ? updatedTransfer : t));
      setSentTransfers(sentTransfers.map(t => t.id === id ? updatedTransfer : t));
      setReceivedTransfers(receivedTransfers.map(t => t.id === id ? updatedTransfer : t));
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка обновления статуса');
    }
  };
  
  const handleAcceptTransfer = (id: string) => {
    handleUpdateStatus(id, TransferStatus.COMPLETED);
  };
  
  const handleRejectTransfer = (id: string) => {
    handleUpdateStatus(id, TransferStatus.REJECTED);
  };
  
  const handleCancelTransfer = (id: string) => {
    handleUpdateStatus(id, TransferStatus.CANCELLED);
  };
  
  const columns: Column[] = [
    {
      id: 'id',
      label: 'ID',
      format: (value) => value.substring(0, 8) + '...',
    },
    {
      id: 'sourceUser',
      label: 'Отправитель',
      format: (value) => value?.fullName || '-',
    },
    {
      id: 'targetUser',
      label: 'Получатель',
      format: (value) => value?.fullName || '-',
    },
    {
      id: 'description',
      label: 'Описание',
      format: (value) => value || '-',
    },
    {
      id: 'status',
      label: 'Статус',
      format: (value) => renderStatus(value),
    },
    {
      id: 'items',
      label: 'Кол-во элементов',
      format: (value) => value?.length || 0,
    },
    {
      id: 'createdAt',
      label: 'Дата создания',
      format: (value) => dayjs(value).format('DD.MM.YYYY HH:mm'),
    },
  ];
  
  const allActions: Action[] = [
    {
      icon: <ViewIcon />,
      tooltip: 'Просмотр',
      onClick: handleViewTransfer,
    },
  ];
  
  const sentActions: Action[] = [
    {
      icon: <ViewIcon />,
      tooltip: 'Просмотр',
      onClick: handleViewTransfer,
    },
    {
      icon: <CancelIcon />,
      tooltip: 'Отменить',
      onClick: handleCancelTransfer,
      color: 'error',
      disabled: (row) => row.status !== TransferStatus.PENDING,
      hide: (row) => row.status !== TransferStatus.PENDING,
    },
  ];
  
  const receivedActions: Action[] = [
    {
      icon: <ViewIcon />,
      tooltip: 'Просмотр',
      onClick: handleViewTransfer,
    },
    {
      icon: <CheckCircleIcon />,
      tooltip: 'Принять',
      onClick: handleAcceptTransfer,
      color: 'success',
      disabled: (row) => row.status !== TransferStatus.PENDING,
      hide: (row) => row.status !== TransferStatus.PENDING,
    },
    {
      icon: <BlockIcon />,
      tooltip: 'Отклонить',
      onClick: handleRejectTransfer,
      color: 'error',
      disabled: (row) => row.status !== TransferStatus.PENDING,
      hide: (row) => row.status !== TransferStatus.PENDING,
    },
  ];
  
  const renderStatus = (status: TransferStatus) => {
    const configs = {
      [TransferStatus.PENDING]: { color: 'warning', label: 'В ожидании', bg: '#ff9800' },
      [TransferStatus.COMPLETED]: { color: 'success', label: 'Завершено', bg: '#4caf50' },
      [TransferStatus.REJECTED]: { color: 'error', label: 'Отклонено', bg: '#f44336' },
      [TransferStatus.CANCELLED]: { color: 'default', label: 'Отменено', bg: '#9e9e9e' },
    };
    
    const config = configs[status] || configs[TransferStatus.PENDING];
    
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{
          bgcolor: `${config.bg}20`,
          color: config.bg,
          fontWeight: 600,
          border: `1px solid ${config.bg}40`
        }}
      />
    );
  };
  
  const pendingReceivedCount = receivedTransfers.filter(t => t.status === TransferStatus.PENDING).length;
  const pendingSentCount = sentTransfers.filter(t => t.status === TransferStatus.PENDING).length;
  
  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                <TransferIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Передача данных
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Управление передачами данных между пользователями
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTransfer}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Создать передачу
            </Button>
          </Box>
        </Box>
      </Fade>
      
      <Fade in={mounted} timeout={600}>
        <Card sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                  },
                  '&.Mui-selected': {
                    color: '#667eea',
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListIcon />
                    Все передачи
                    <Chip 
                      label={allTransfers.length} 
                      size="small" 
                      sx={{ 
                        height: 20,
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        color: '#667eea',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SendIcon />
                    Отправленные
                    <Badge badgeContent={pendingSentCount} color="warning">
                      <Chip 
                        label={sentTransfers.length} 
                        size="small" 
                        sx={{ 
                          height: 20,
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontWeight: 600
                        }}
                      />
                    </Badge>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InboxIcon />
                    Полученные
                    <Badge badgeContent={pendingReceivedCount} color="error">
                      <Chip 
                        label={receivedTransfers.length} 
                        size="small" 
                        sx={{ 
                          height: 20,
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontWeight: 600
                        }}
                      />
                    </Badge>
                  </Box>
                } 
              />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <DataTable
              columns={columns}
              data={allTransfers}
              loading={loading.all}
              actions={allActions}
              emptyText="Нет доступных передач"
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <DataTable
              columns={columns}
              data={sentTransfers}
              loading={loading.sent}
              actions={sentActions}
              emptyText="Нет отправленных передач"
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <DataTable
              columns={columns}
              data={receivedTransfers}
              loading={loading.received}
              actions={receivedActions}
              emptyText="Нет полученных передач"
            />
          </TabPanel>
        </Card>
      </Fade>
      
      <AlertMessage 
        open={!!error} 
        message={error || ''} 
        severity="error" 
        onClose={() => setError(null)} 
      />
    </Box>
  );
}